/**
 * @file MappingDialog.js
 * @description Boîte de dialogue pour l'édition de mappings.
 *
 * Permet de:
 * - Basculer entre deux modes de mapping: objet (clé-valeur) et valeur simple
 * - Éditer visuellement le mapping avec MappingContent
 * - Valider ou annuler les modifications
 *
 * Formats de sortie:
 * - OUTPUT_OBJECT: Mapping sous forme d'objet JSON { "key": "value" }
 * - OUTPUT_VALUE: Mapping sous forme de valeur simple (handlebars ou hardcodé)
 */
import { useCallback, useEffect, useState } from "react";
import lod_ from "lodash";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select
} from "@mui/material";
import MDButton from "components/Basics/MDButton";
import { t } from "i18next";
import MappingContent from "./MappingContent";
import MDTypography from "components/Basics/MDTypography";
import MDBox from "components/Basics/MDBox";

/**
 * Formats de sortie disponibles pour le mapping.
 * @constant
 */
const OUTPUT_FORMATS = {
	/** Format objet: { "key1": "value1", "key2": "value2" } */
	OUTPUT_OBJECT: "output_object",
	/** Format valeur simple: {{{ json context.user }}} ou "hardcoded value" */
	OUTPUT_VALUE: "output_value"
};

/**
 * Boîte de dialogue pour éditer un mapping.
 *
 * Offre un sélecteur pour choisir le format (objet ou valeur) et
 * un éditeur visuel pour construire le mapping.
 *
 * @param {Object} props
 * @param {boolean} props.open - État d'ouverture de la dialogue
 * @param {Function} props.onClose - Callback appelé lors de la fermeture
 * @param {string} props.mapping - Mapping initial à éditer
 * @param {Object} props.config - Configuration (dictionnaires, etc.)
 * @param {Function} props.onChange - Callback appelé avec le nouveau mapping lors de la validation
 */
const MappingDialog = ({ open, onClose, mapping: mappingProps, config, onChange }) => {
	/** État local du mapping en cours d'édition (isolé du parent jusqu'à validation) */
	const [mapping, setMapping] = useState(lod_.cloneDeep(mappingProps));

	/**
	 * Compteur de rechargement pour forcer la réinitialisation de MappingContent
	 * lors du changement de format (objet <-> valeur)
	 */
	const [reload, setReload] = useState(0);

	/** Indique si le mapping est un objet (true) ou une valeur simple (false) */
	const [mappingIsObject, setMappingIsObject] = useState(true);

	/** Format de sortie sélectionné dans le menu déroulant */
	const [currentOutputFormat, setCurrentOutputFormat] = useState(OUTPUT_FORMATS.OUTPUT_OBJECT);

	/**
	 * Gestionnaire de modification du mapping.
	 * Appelé par MappingContent à chaque modification.
	 *
	 * @param {string} newMapping - Nouveau mapping modifié
	 */
	const handleChangeMapping = useCallback(newMapping => {
		setMapping(newMapping);
	}, []);

	/**
	 * Effet de synchronisation: recharge le mapping depuis les props
	 * à chaque ouverture de la dialogue.
	 *
	 * Permet de réinitialiser l'état local avec la dernière valeur
	 * du parent, annulant les modifications non validées.
	 */
	useEffect(() => {
		if (open === true) {
			setMapping(lod_.cloneDeep(mappingProps));
		}
	}, [open, mappingProps]);

	/**
	 * Effet de synchronisation: met à jour le format sélectionné
	 * dans le menu déroulant quand mappingIsObject change.
	 *
	 * Cette synchronisation est nécessaire car mappingIsObject peut être
	 * modifié par MappingContent (détection automatique du format).
	 */
	useEffect(() => {
		if (mappingIsObject === true) {
			setCurrentOutputFormat(OUTPUT_FORMATS.OUTPUT_OBJECT);
		} else {
			setCurrentOutputFormat(OUTPUT_FORMATS.OUTPUT_VALUE);
		}
	}, [mappingIsObject]);

	/**
	 * Gestionnaire de changement de format.
	 *
	 * Réinitialise le mapping selon le format choisi:
	 * - Objet: {}
	 * - Valeur: ""
	 *
	 * Incrémente le compteur reload pour forcer MappingContent à se réinitialiser.
	 *
	 * @param {string} newFormat - Nouveau format (OUTPUT_OBJECT ou OUTPUT_VALUE)
	 */
	const handleFormatChange = useCallback(newFormat => {
		const isObject = newFormat === OUTPUT_FORMATS.OUTPUT_OBJECT;
		setMappingIsObject(isObject);

		// Réinitialise le mapping selon le format
		if (isObject === true) {
			setMapping("{}");
		} else {
			setMapping("");
		}

		// Force le rechargement de MappingContent
		setReload(prev => prev + 1);
	}, []);

	/**
	 * Gestionnaire de validation.
	 * Propage le mapping modifié au parent et ferme la dialogue.
	 */
	const handleValidate = useCallback(() => {
		onChange(mapping);
		onClose();
	}, [onChange, onClose, mapping]);

	return (
		<Dialog
			open={open}
			fullWidth
			maxWidth="xxl"
			PaperProps={{
				style: {
					height: "90%",
					width: "100%",
					maxWidth: "90%"
				}
			}}
		>
			{/* ==================== EN-TÊTE ==================== */}
			<DialogTitle>
				<MDBox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
					{/* Titre */}
					<MDTypography variant="h4" sx={{ flex: 3 }}>
						Mapping
					</MDTypography>

					{/* Sélecteur de format */}
					<MDBox flex="1">
						<FormControl fullWidth>
							<InputLabel id="select-label">Format du mapping</InputLabel>
							<Select
								labelId="select-label"
								id="select"
								label="Format du mapping"
								value={currentOutputFormat}
								onChange={e => handleFormatChange(e.target.value)}
							>
								<MenuItem value={OUTPUT_FORMATS.OUTPUT_OBJECT}>Sortie en objet</MenuItem>
								<MenuItem value={OUTPUT_FORMATS.OUTPUT_VALUE}>Sortie en valeur simple</MenuItem>
							</Select>
						</FormControl>
					</MDBox>
				</MDBox>
			</DialogTitle>

			{/* ==================== CONTENU - ÉDITEUR DE MAPPING ==================== */}
			<DialogContent>
				<MappingContent
					mapping={mapping}
					mappingIsObject={mappingIsObject}
					setMappingIsObject={setMappingIsObject}
					reload={reload}
					config={config}
					handleChangeMapping={handleChangeMapping}
				/>
			</DialogContent>

			{/* ==================== ACTIONS - ANNULER / VALIDER ==================== */}
			<DialogActions>
				{/* Bouton Annuler: ferme sans sauvegarder */}
				<MDButton onClick={onClose} variant="outlined" color="info">
					{t("FORMS.cancel")}
				</MDButton>

				{/* Bouton Valider: sauvegarde et ferme */}
				<MDButton onClick={handleValidate} color="info" variant="contained">
					{t("FORMS.validate")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default MappingDialog;
